import type { Element } from '@/types';

// Storage keys
const STORAGE_KEYS = {
  CANVAS_ELEMENTS: 'canvasDraw_elements',
  VIEWPORT_STATE: 'canvasDraw_viewport',
  APP_PREFERENCES: 'canvasDraw_preferences',
} as const;

// Canvas state persistence
export const saveCanvasElements = (elements: Element[]): void => {
  try {
    const serializedElements = JSON.stringify(elements);
    localStorage.setItem(STORAGE_KEYS.CANVAS_ELEMENTS, serializedElements);
  } catch (error) {
    console.warn('Failed to save canvas elements to localStorage:', error);
  }
};

export const loadCanvasElements = (): Element[] => {
  try {
    const serializedElements = localStorage.getItem(
      STORAGE_KEYS.CANVAS_ELEMENTS
    );
    if (serializedElements) {
      return JSON.parse(serializedElements);
    }
  } catch (error) {
    console.warn('Failed to load canvas elements from localStorage:', error);
  }
  return [];
};

// Viewport state persistence
interface ViewportState {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

export const saveViewportState = (viewport: ViewportState): void => {
  try {
    const serializedViewport = JSON.stringify(viewport);
    localStorage.setItem(STORAGE_KEYS.VIEWPORT_STATE, serializedViewport);
  } catch (error) {
    console.warn('Failed to save viewport state to localStorage:', error);
  }
};

export const loadViewportState = (): ViewportState | null => {
  try {
    const serializedViewport = localStorage.getItem(
      STORAGE_KEYS.VIEWPORT_STATE
    );
    if (serializedViewport) {
      return JSON.parse(serializedViewport);
    }
  } catch (error) {
    console.warn('Failed to load viewport state from localStorage:', error);
  }
  return null;
};

// App preferences persistence
interface AppPreferences {
  autoSave: boolean;
  saveInterval: number; // in milliseconds
  theme: 'light' | 'dark';
  snapToGrid: boolean;
  gridSize: number;
}

const defaultPreferences: AppPreferences = {
  autoSave: true,
  saveInterval: 30000, // 30 seconds
  theme: 'dark',
  snapToGrid: false,
  gridSize: 10,
};

export const saveAppPreferences = (
  preferences: Partial<AppPreferences>
): void => {
  try {
    const currentPreferences = loadAppPreferences();
    const updatedPreferences = { ...currentPreferences, ...preferences };
    const serializedPreferences = JSON.stringify(updatedPreferences);
    localStorage.setItem(STORAGE_KEYS.APP_PREFERENCES, serializedPreferences);
  } catch (error) {
    console.warn('Failed to save app preferences to localStorage:', error);
  }
};

export const loadAppPreferences = (): AppPreferences => {
  try {
    const serializedPreferences = localStorage.getItem(
      STORAGE_KEYS.APP_PREFERENCES
    );
    if (serializedPreferences) {
      const loadedPreferences = JSON.parse(serializedPreferences);
      return { ...defaultPreferences, ...loadedPreferences };
    }
  } catch (error) {
    console.warn('Failed to load app preferences from localStorage:', error);
  }
  return defaultPreferences;
};

// Clear all stored data
export const clearAllStoredData = (): void => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Failed to clear stored data:', error);
  }
};

// Check if localStorage is available
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

// Auto-save functionality
let autoSaveTimer: NodeJS.Timeout | null = null;

export const setupAutoSave = (
  getElements: () => Element[],
  getViewport: () => ViewportState,
  interval: number = 30000
): void => {
  if (autoSaveTimer) {
    clearInterval(autoSaveTimer);
  }

  autoSaveTimer = setInterval(() => {
    saveCanvasElements(getElements());
    saveViewportState(getViewport());
  }, interval);
};

export const clearAutoSave = (): void => {
  if (autoSaveTimer) {
    clearInterval(autoSaveTimer);
    autoSaveTimer = null;
  }
};

// Export storage keys for external use
export { STORAGE_KEYS };
